import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Sheet } from '@/components/common/Sheet';
import { useLandingNotice } from '@/contexts/LandingNoticeContext';

/**
 * ADR-0115 — LA modale di atterraggio. Una sola, per ogni tipo di link.
 *
 * ═══ COSA CAMBIA RISPETTO ALLA SCHERMATA DI PRIMA ═══
 * Niente, nel contenuto: le stesse frasi, le stesse cause distinte. Cambia la
 * POSIZIONE. Prima l'utente atterrava su una pagina che diceva la verita' e
 * chiedeva un secondo atto («vai alla Hub») per arrivare dove sarebbe potuto
 * essere subito. Ora e' gia' li': chiudere non porta da nessuna parte, perche'
 * sotto c'e' gia' un posto utile. *L'informazione era giusta, la posizione era
 * sbagliata* (L-304, misurata da Luca dal telefono).
 *
 * ═══ 🔴 CHIUSA SENZA ESSERE LETTA — COSA SUCCEDE, DICHIARATO ═══
 * Il velo, il trascinamento verso il basso, «Escape» e il tasto indietro
 * chiudono (`Sheet.tsx`). Chiudere NON e' recuperabile: l'avviso non e'
 * persistito e lo stash del payload e' gia' stato consumato a monte
 * (`usePendingInvite.ts:42`, che cancella PRIMA di navigare, di proposito).
 * ⇒ Un tocco distratto sul velo perde il messaggio per sempre; il link, no —
 *   riaprirlo da Telegram ricomincia l'atterraggio da capo.
 * ⚠️ E' esattamente il rischio che l'ADR §Conseguenze mette in conto senza
 * risolverlo. La contromisura prevista e' l'unica applicata qui: **l'azione sta
 * DENTRO**, ed e' il bottone piu' evidente — cosi' il tocco distratto piu'
 * probabile fa la cosa giusta invece di far sparire tutto. ⛔ Non e' una
 * soluzione, e' una riduzione del danno: dichiararla per quello che e' serve a
 * non far credere a nessuno che il caso sia coperto.
 */
export function LandingNoticeModal() {
  const { t } = useTranslation('landing');
  const { entry, dismiss } = useLandingNotice();
  const navigate = useNavigate();

  const notice = entry?.notice ?? null;
  const action = notice?.action ?? null;
  // `retry` ri-atterra sulla stessa risorsa; `navigate` porta altrove. Se chi ha
  // alzato l'avviso non ha detto DOVE si riprova, l'azione non si mostra invece
  // di mostrare un bottone che non fa niente.
  const actionTo = action?.kind === 'retry' ? entry?.retryTo : action?.to;
  const showAction = action != null && typeof actionTo === 'string' && actionTo.length > 0;

  return (
    <Sheet open={notice != null} onClose={dismiss} label={notice ? t(notice.titleKey) : undefined}>
      {notice && (
        <div
          className="flex flex-col gap-4 px-6 pb-2 pt-4 text-center"
          data-testid="landing-notice"
        >
          <h2 className="text-lg font-bold text-fg" data-testid="landing-notice-title">
            {t(notice.titleKey)}
          </h2>
          <p className="text-sm text-fg-muted" data-testid="landing-notice-body">
            {t(notice.bodyKey)}
          </p>

          <div className="flex flex-col gap-2 pt-2">
            {/* L'AZIONE PER PRIMA E PIU' EVIDENTE. L'ordine e' il meccanismo:
                v. il blocco «CHIUSA SENZA ESSERE LETTA» in testa al file. */}
            {showAction && (
              <button
                type="button"
                data-testid="landing-notice-action"
                onClick={() => {
                  dismiss();
                  navigate(actionTo as string);
                }}
                className="rounded-md bg-brand px-5 py-3 text-sm font-medium text-brand-fg transition-opacity active:opacity-80"
              >
                {t(action.labelKey)}
              </button>
            )}
            {/* «Chiudi», non «vai alla Hub»: sotto c'e' gia' la superficie viva,
                e nominare una destinazione suggerirebbe uno spostamento che non
                avviene. */}
            <button
              type="button"
              data-testid="landing-notice-dismiss"
              onClick={dismiss}
              className="rounded-md px-5 py-2 text-sm text-fg-muted"
            >
              {t('dismiss')}
            </button>
          </div>
        </div>
      )}
    </Sheet>
  );
}
